// One attendance percentage for the whole product (OEL-158).
//
// Before this, six formulas disagreed: some credited Late, some did not; some
// excluded Leave and Holiday from the denominator, some counted them; the
// parent app recomputed its own rate next to the server's. The rules live here
// so a percentage means the same thing on every screen.
//
// The weights are a school setting, defaulting to the commonest of the old
// server rules: Late a full day, Half Day none.
//
// Adopting one rule DOES move numbers, and pretending otherwise would send
// support chasing ghosts. At default weights, against the code this replaces:
//   - the class report and the parent app now credit Late, which they alone
//     did not, so those percentages rise;
//   - subject-wise, class-wise analytics and chronic absentees now leave
//     Holiday out of the denominator, which only some of them did, so those
//     percentages rise, and a shorter chronic-absentee list is the direct
//     consequence of that, not of anyone's attendance changing;
//   - "Late with Excuse" counts as Late everywhere, where before it did on the
//     portal and not in the admin report.
// Nothing about the stored marks changes, so setting a school's weights back
// reproduces the old arithmetic exactly.

export interface AttendanceWeights {
  /** How much of a day a Late mark is worth: 0, 0.5 or 1. */
  late: number;
  /** How much of a day a Half Day mark is worth: 0, 0.5 or 1. */
  half_day: number;
}

export const DEFAULT_ATTENDANCE_WEIGHTS: AttendanceWeights = { late: 1, half_day: 0 };

export const ATTENDANCE_WEIGHT_CHOICES = [0, 0.5, 1] as const;

/** Only 0, 0.5 and 1 are allowed; anything else falls back to the default. */
export function normaliseWeight(v: unknown, fallback: number): number {
  const n = Number(v);
  if (!Number.isFinite(n)) return fallback;
  return (ATTENDANCE_WEIGHT_CHOICES as readonly number[]).includes(n) ? n : fallback;
}

export function normaliseWeights(v: Partial<AttendanceWeights> | null | undefined): AttendanceWeights {
  return {
    late: normaliseWeight(v?.late, DEFAULT_ATTENDANCE_WEIGHTS.late),
    half_day: normaliseWeight(v?.half_day, DEFAULT_ATTENDANCE_WEIGHTS.half_day),
  };
}

export type AttendanceKind = 'present' | 'late' | 'absent' | 'halfday' | 'leave' | 'holiday' | 'other';

/**
 * Map an attendence_type.type name to the kind the percentage understands.
 * Schools may add their own types; those count as marked days that were not
 * attended rather than being dropped, which is what the old code did by
 * accident in some places and deliberately in others.
 */
export function attendanceKind(typeName: unknown): AttendanceKind {
  const t = String(typeName ?? '').trim().toLowerCase();
  if (!t) return 'other';
  if (t === 'present') return 'present';
  if (t === 'absent') return 'absent';
  // Half day is tested before late on purpose: a school type named
  // "Late Half Day" is a half day, and reading it as a Late would hand out a
  // full day's credit for half a day's attendance.
  if (/half[\s-]?day/.test(t)) return 'halfday';
  // "Leave" and "Holiday" as the last word: "Sick Leave", "Medical Leave" and
  // "Public Holiday" are days the school did not expect the student, exactly
  // like the plain types, and counting them against anyone would be wrong.
  if (/(^|[\s-])leave$/.test(t)) return 'leave';
  if (/(^|[\s-])holiday$/.test(t)) return 'holiday';
  // "Late with Excuse" and friends. It was credited in the portal and ignored
  // in the admin report; it is a Late everywhere now.
  if (/(^|[\s-])late([\s-]|$)/.test(t)) return 'late';
  return 'other';
}

export interface AttendanceCounts {
  present: number;
  absent: number;
  late: number;
  halfday: number;
  leave: number;
  holiday: number;
  total: number;
  other?: number;
}

export function emptyAttendanceCounts(): AttendanceCounts {
  return { present: 0, absent: 0, late: 0, halfday: 0, leave: 0, holiday: 0, other: 0, total: 0 };
}

/** Add one mark to a counter bag. Returns the same bag for convenience. */
export function countAttendance(counts: AttendanceCounts, typeName: unknown): AttendanceCounts {
  const kind = attendanceKind(typeName);
  counts.total++;
  if (kind === 'present') counts.present++;
  else if (kind === 'late') counts.late++;
  else if (kind === 'absent') counts.absent++;
  else if (kind === 'halfday') counts.halfday++;
  else if (kind === 'leave') counts.leave++;
  else if (kind === 'holiday') counts.holiday++;
  else counts.other = (counts.other || 0) + 1;
  return counts;
}

/**
 * Days a student is credited with attending. Fractional when the school gives
 * Late or Half Day a half weight; the raw counts stay whole numbers.
 */
export function attendedDays(c: AttendanceCounts, w: AttendanceWeights = DEFAULT_ATTENDANCE_WEIGHTS): number {
  return c.present + c.late * w.late + c.halfday * w.half_day;
}

/**
 * Days that could have been attended. Leave and Holiday are days the school
 * did not expect the student, so they are excluded rather than counted against
 * anyone. Every report uses this denominator now.
 */
export function expectedDays(c: AttendanceCounts): number {
  return c.total - c.leave - c.holiday;
}

/** The percentage, rounded to `dp` decimals. Zero expected days reads as 0. */
export function attendancePercentage(
  c: AttendanceCounts,
  w: AttendanceWeights = DEFAULT_ATTENDANCE_WEIGHTS,
  dp = 1,
): number {
  const expected = expectedDays(c);
  if (expected <= 0) return 0;
  return +((attendedDays(c, w) / expected) * 100).toFixed(dp);
}

/** A short line a report can print so the reader knows how the % was reached. */
export function describeAttendanceWeights(w: AttendanceWeights): string {
  const part = (label: string, weight: number) =>
    `${label} counts as ${weight === 1 ? 'a full day' : weight === 0.5 ? 'half a day' : 'absent'}`;
  return `${part('Late', w.late)}; ${part('Half Day', w.half_day)}. ` +
    'Leave and Holiday types are left out of the calculation entirely.';
}
